import {
  Alert,
  Button,
  Chip,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { CategorySelect } from './CategorySelect'
import { describeRule, describeScope, formatPercent, PAGE_SIZE } from './helpers'
import { useMappings } from './useProductCategoryData'
import { useProductCategories } from '../../EditQuote/lines/productCategories'
import type { MappingFilters } from './ProductCategories.types'
import './SupplierMappingsTab.css'

const SUPPLIERS = [
  { value: 'TDSYNNEX', label: 'TD SYNNEX' },
  { value: 'INGRAM', label: 'Ingram Micro' },
  { value: 'DELL', label: 'Dell' },
  { value: 'CARAHSOFT', label: 'Carahsoft' },
]

export function SupplierMappingsTab() {
  const [page, setPage] = useState(0)
  const [filters, setFilters] = useState<MappingFilters>({ source: '', search: '', needsReview: true })
  const { rows, total, loading, error, update } = useMappings(filters, page)
  const categories = useProductCategories()
  const isAssignable = (name: string) => categories.some(category => category.name === name)

  useEffect(() => {
    if (!loading && rows.length === 0 && page > 0) setPage(prev => prev - 1)
  }, [loading, rows.length, page])

  const changeFilters = (changes: Partial<MappingFilters>) => {
    setFilters(prev => ({ ...prev, ...changes }))
    setPage(0)
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        How supplier categories map to IMPRES Product Categories. Rules run lowest priority first; the first match wins.
        Changing a rule affects every part that uses it.
      </Typography>
      <Stack direction="row" spacing={2}>
        <FormControl size="small" className="supplier-mappings-tab-supplier">
          <InputLabel>Supplier</InputLabel>
          <Select label="Supplier" value={filters.source} onChange={e => changeFilters({ source: e.target.value })}>
            <MenuItem value="">All</MenuItem>
            {SUPPLIERS.map(supplier => (
              <MenuItem key={supplier.value} value={supplier.value}>
                {supplier.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Search code or label"
          value={filters.search}
          onChange={e => changeFilters({ search: e.target.value })}
        />
        <Button
          variant={filters.needsReview ? 'contained' : 'outlined'}
          onClick={() => changeFilters({ needsReview: !filters.needsReview })}
        >
          {filters.needsReview ? 'Showing: needs review' : 'Showing: all'}
        </Button>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {loading && <LinearProgress />}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Supplier</TableCell>
              <TableCell>Rule</TableCell>
              <TableCell>Supplier label</TableCell>
              <TableCell align="right">Catalog lines</TableCell>
              <TableCell>Category</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.id} className={row.isActive ? undefined : 'supplier-mappings-tab-inactive'}>
                <TableCell>{row.source}</TableCell>
                <TableCell>
                  <Tooltip title={describeRule(row)}>
                    <Typography variant="body2" className="supplier-mappings-tab-pattern">
                      {row.pattern}
                    </Typography>
                  </Tooltip>
                  {row.scope && (
                    <Typography variant="caption" color="text.secondary">
                      {describeScope(row.scope)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell className="supplier-mappings-tab-label">{row.sourceLabel ?? '-'}</TableCell>
                <TableCell align="right">{row.lineCount?.toLocaleString('en-US') ?? '-'}</TableCell>
                <TableCell>
                  <CategorySelect
                    value={row.productCategory}
                    onChange={name => void update(row.id, { productCategory: name })}
                  />
                  {!row.reviewedBy && (
                    <Chip size="small" label={formatPercent(row.confidence)} className="supplier-mappings-tab-chip" />
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    {!row.reviewedBy && (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!isAssignable(row.productCategory)}
                        onClick={() => void update(row.id, { reviewed: true })}
                      >
                        Approve
                      </Button>
                    )}
                    <Button size="small" onClick={() => void update(row.id, { isActive: !row.isActive })}>
                      {row.isActive ? 'Disable' : 'Enable'}
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>No rules match these filters.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[PAGE_SIZE]}
        onPageChange={(_e, next) => setPage(next)}
      />
    </Stack>
  )
}
